/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useRef, useImperativeHandle, useCallback } from 'react';
import { resolveStatementAttachmentHref } from '../utils/statementAttachments';
import { runPython } from './PyodideRunner';

const RUNNER_KEY = 'runner';

/** Плоское имя файла для виртуальной ФС Pyodide (без путей). */
export function sanitizeRunnerFileKey(raw) {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const base = trimmed.replace(/\\/g, '/').split('/').filter(Boolean).pop() ?? '';
  if (!base || base === '.' || base === '..' || base.includes('..')) return null;
  return base;
}

function uniqueRunnerFileName(desired, existingKeys) {
  const set = new Set(existingKeys);
  if (!set.has(desired)) return desired;
  const dot = desired.lastIndexOf('.');
  const stem = dot > 0 ? desired.slice(0, dot) : desired;
  const ext = dot > 0 ? desired.slice(dot) : '';
  let i = 2;
  let candidate;
  do {
    candidate = `${stem}_${i}${ext}`;
    i += 1;
  } while (set.has(candidate));
  return candidate;
}

/** Очередь URL: в БД часто /media/ege17/…, файлы на диске — media/17data/… */
function attachmentMediaFetchUrls(attUrl) {
  const primary = resolveStatementAttachmentHref(attUrl);
  const list = [primary];
  const raw = typeof attUrl === 'string' ? attUrl.trim() : '';
  const seg = raw.split('/').filter(Boolean);
  const tail = seg.length ? seg[seg.length - 1] : '';
  const base = sanitizeRunnerFileKey(tail);
  if (base) {
    const alt = `/media/17data/${base}`;
    if (!list.includes(alt)) list.push(alt);
  }
  return list;
}

/**
 * Загружает вложения задачи по URL; частичный успех: ошибки по отдельным элементам в loadErrors.
 * @param {Array<{ name?: string, url?: string }>} attachments
 * @returns {Promise<{ files: Record<string, string>, loadErrors: Array<{ name: string, message: string }> }>}
 */
export async function fetchStatementAttachmentsAsFiles(attachments) {
  if (!Array.isArray(attachments) || attachments.length === 0) {
    return { files: {}, loadErrors: [] };
  }
  const files = {};
  const loadErrors = [];
  for (const att of attachments) {
    const rawName = att?.name;
    const safe = sanitizeRunnerFileKey(typeof rawName === 'string' ? rawName : '');
    const urlStr = typeof att?.url === 'string' ? att.url.trim() : '';
    const hasUrl = Boolean(urlStr);

    if (!safe && !hasUrl) {
      loadErrors.push({
        name: '—',
        message: 'Вложение без имени файла и без URL.',
      });
      continue;
    }
    if (!safe) {
      const fromUrl = sanitizeRunnerFileKey(urlStr.split('/').pop() || '') || 'файл';
      loadErrors.push({
        name: fromUrl,
        message: 'Не указано имя файла во вложении.',
      });
      continue;
    }
    if (!hasUrl) {
      loadErrors.push({
        name: safe,
        message: 'Не указан URL файла.',
      });
      continue;
    }

    const key = uniqueRunnerFileName(safe, Object.keys(files));
    try {
      const candidates = attachmentMediaFetchUrls(att.url);
      let lastStatus = 0;
      let loadedText = null;
      for (const abs of candidates) {
        if (!abs) continue;
        const res = await fetch(abs, { credentials: 'include' });
        lastStatus = res.status;
        if (res.ok) {
          loadedText = await res.text();
          break;
        }
      }
      if (loadedText == null) {
        loadErrors.push({
          name: key,
          message: `Не удалось загрузить «${key}» (HTTP ${lastStatus || '?'}). Проверьте путь и раздачу /media на сервере.`,
        });
      } else {
        files[key] = loadedText;
      }
    } catch (e) {
      const msg = e?.message || 'неизвестная ошибка';
      loadErrors.push({
        name: key,
        message: `Ошибка при загрузке «${key}»: ${msg}`,
      });
    }
  }
  return { files, loadErrors };
}

const RunnerContext = createContext(null);

export function useRunnerContext() {
  const ctx = useContext(RunnerContext);
  if (!ctx) throw new Error('useRunnerContext must be used within CollaborativeRunnerProvider');
  return ctx;
}

const MAX_OUTPUT_LINES = 200;

function trimOutputLines(text) {
  if (!text) return '';
  const lines = text.split('\n');
  if (lines.length <= MAX_OUTPUT_LINES) return text;
  return lines.slice(-MAX_OUTPUT_LINES).join('\n');
}

const AUTO_CLEAR_STORAGE_KEY = 'workAutoClearOutput';

function getStoredAutoClear() {
  try {
    return localStorage.getItem(AUTO_CLEAR_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function CollaborativeRunnerProvider({ code, ydoc, runRef, children }) {
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [stdin, setStdin] = useState('');
  const [files, setFiles] = useState({}); // { "имя.txt": "содержимое", ... }
  const [attachmentLoadErrors, setAttachmentLoadErrors] = useState(
    /** @type {Array<{ name: string, message: string }>} */ ([]),
  );
  const [outputSelection, setOutputSelection] = useState({ start: 0, end: 0 });
  const [lastRunStartedAt, setLastRunStartedAt] = useState(null);
  const [lastRunFinishedAt, setLastRunFinishedAt] = useState(null);
  const [lastRunDurationMs, setLastRunDurationMs] = useState(null);
  const [runElapsedMs, setRunElapsedMs] = useState(null);
  const [autoClearOutput, setAutoClearOutputState] = useState(getStoredAutoClear);
  const [codeAtLastRun, setCodeAtLastRun] = useState(null);
  const fileInputRef = useRef(null);
  const outputRef = useRef(null);
  const runStartedAtRef = useRef(null);
  const abortControllerRef = useRef(null);

  const setAutoClearOutput = (v) => {
    setAutoClearOutputState(v);
    try {
      localStorage.setItem(AUTO_CLEAR_STORAGE_KEY, String(!!v));
    } catch {
      // ignore
    }
  };

  const clearOutput = () => {
    setOutput('');
    setError('');
    if (ydoc) {
      const m = ydoc.getMap(RUNNER_KEY);
      m.set('output', '');
      m.set('error', '');
    }
  };

  useEffect(() => {
    if (!loading) return;
    runStartedAtRef.current = Date.now();
    const t = setInterval(() => {
      if (runStartedAtRef.current) setRunElapsedMs(Date.now() - runStartedAtRef.current);
    }, 100);
    return () => clearInterval(t);
  }, [loading]);

  const runAndSync = async (stdinVal, filesVal) => {
    const started = Date.now();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const appendStdoutChunk = (chunk) => {
      if (typeof chunk !== 'string' || !chunk) return;
      setOutput((prev) => {
        const next = trimOutputLines((prev || '') + chunk);
        if (ydoc) {
          const m = ydoc.getMap(RUNNER_KEY);
          m.set('output', next);
          // If we're still producing stdout, don't keep a stale error.
          m.set('error', '');
        }
        return next;
      });
    };

    setLoading(true);
    setError('');
    setLastRunStartedAt(started);
    setLastRunFinishedAt(null);
    setLastRunDurationMs(null);
    setRunElapsedMs(0);
    setCodeAtLastRun(code ?? '');
    if (ydoc) {
      const m = ydoc.getMap(RUNNER_KEY);
      m.set('loading', true);
    }
    try {
      const result = await runPython(code || '', stdinVal, filesVal, {
        signal: controller.signal,
        onStdoutChunk: appendStdoutChunk,
      });
      const finished = Date.now();
      const durationMs = finished - started;
      setLastRunFinishedAt(finished);
      setLastRunDurationMs(durationMs);
      setRunElapsedMs(null);
      if (result.success) {
        const trimmed = trimOutputLines(result.output || '');
        setOutput(trimmed);
        setError('');
        if (ydoc) {
          const m = ydoc.getMap(RUNNER_KEY);
          m.set('output', trimmed);
          m.set('error', '');
          m.set('loading', false);
          m.set('stdin', stdinVal || '');
          try { m.set('filesJson', JSON.stringify(filesVal || {})); } catch { /* ignore */ }
        }
      } else {
        const errMsg = result.error || 'Ошибка выполнения';
        setError(errMsg);
        const trimmed = trimOutputLines(result.output || '');
        setOutput(trimmed);
        if (ydoc) {
          const m = ydoc.getMap(RUNNER_KEY);
          m.set('error', errMsg);
          m.set('output', trimmed);
          m.set('loading', false);
        }
      }
    } catch (e) {
      const finished = Date.now();
      setLastRunFinishedAt(finished);
      setLastRunDurationMs(finished - started);
      setRunElapsedMs(null);
      if (e?.name === 'AbortError') {
        setError('');
        const stoppedMsg = 'Остановлено';
        setOutput((prev) => (prev ? `${prev}\n\n${stoppedMsg}` : stoppedMsg));
        if (ydoc) {
          const m = ydoc.getMap(RUNNER_KEY);
          m.set('error', '');
          m.set('output', stoppedMsg);
          m.set('loading', false);
        }
      } else {
        const errMsg = e.message || 'Ошибка выполнения';
        setError(errMsg);
        setOutput('');
        if (ydoc) {
          const m = ydoc.getMap(RUNNER_KEY);
          m.set('error', errMsg);
          m.set('output', '');
          m.set('loading', false);
        }
      }
    } finally {
      setLoading(false);
      setRunElapsedMs(null);
      abortControllerRef.current = null;
    }
  };

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const handleRun = async () => {
    const effectiveFiles = {};
    Object.entries(files || {}).forEach(([name, content]) => {
      const key = sanitizeRunnerFileKey(name);
      if (!key || typeof content !== 'string' || !content.trim()) return;
      effectiveFiles[key] = content;
    });
    if (autoClearOutput) {
      clearOutput();
    } else if (output || error) {
      const nextOutput = output ? `${output}\n\n-----\n` : '';
      setOutput(nextOutput);
      setError('');
      if (ydoc) {
        const m = ydoc.getMap(RUNNER_KEY);
        m.set('output', nextOutput);
        m.set('error', '');
      }
    }
    if (ydoc) {
      const m = ydoc.getMap(RUNNER_KEY);
      m.set('loading', true);
      m.set('stdin', stdin || '');
      try { m.set('filesJson', JSON.stringify(files || {})); } catch { /* ignore */ }
    }
    await runAndSync(stdin, effectiveFiles);
  };

  useImperativeHandle(runRef, () => ({ run: handleRun }), [code, stdin, files, ydoc]);

  useEffect(() => {
    if (!ydoc) return;
    const m = ydoc.getMap(RUNNER_KEY);
    const apply = () => {
      const o = m.get('output');
      const e = m.get('error');
      const l = m.get('loading');
      const s = m.get('stdin');
      const filesJson = m.get('filesJson');
      const selStart = m.get('outputSelectionStart');
      const selEnd = m.get('outputSelectionEnd');
      if (o !== undefined) setOutput(trimOutputLines(o));
      if (e !== undefined) setError(e);
      if (l !== undefined) setLoading(l);
      if (s !== undefined) setStdin(s);
      if (typeof filesJson === 'string') {
        try {
          const parsed = JSON.parse(filesJson);
          if (parsed && typeof parsed === 'object') {
            setFiles(parsed);
            setAttachmentLoadErrors((prev) =>
              prev.filter((err) => {
                const v = parsed[err.name];
                return !(typeof v === 'string' && v.trim());
              }),
            );
          }
        } catch {
          // ignore
        }
      }
      if (typeof selStart === 'number' && typeof selEnd === 'number') {
        setOutputSelection({ start: Math.max(0, selStart), end: Math.max(0, selEnd) });
      }
    };
    apply();
    m.observe(apply);
    return () => m.unobserve(apply);
  }, [ydoc]);

  const updateSelectionFromDom = () => {
    if (!outputRef.current) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!outputRef.current.contains(range.startContainer) || !outputRef.current.contains(range.endContainer)) return;
    let start = range.startOffset;
    let end = range.endOffset;
    if (start > end) [start, end] = [end, start];
    const preText = (output || '').toString();
    start = Math.max(0, Math.min(start, preText.length));
    end = Math.max(0, Math.min(end, preText.length));
    const sel = { start, end };
    setOutputSelection(sel);
    if (ydoc) {
      const m = ydoc.getMap(RUNNER_KEY);
      m.set('outputSelectionStart', sel.start);
      m.set('outputSelectionEnd', sel.end);
    }
  };

  const setStdinVal = (val) => {
    setStdin(val);
    if (ydoc) {
      const m = ydoc.getMap(RUNNER_KEY);
      m.set('stdin', val);
    }
  };
  const setFilesVal = useCallback((next) => {
    setFiles(next);
    if (ydoc) {
      const m = ydoc.getMap(RUNNER_KEY);
      try { m.set('filesJson', JSON.stringify(next || {})); } catch { /* ignore */ }
    }
  }, [ydoc]);

  const codeEditedSinceLastRun = Boolean(lastRunStartedAt && (code ?? '') !== (codeAtLastRun ?? ''));

  const ctx = {
    output,
    error,
    loading,
    running: loading,
    lastRunStartedAt,
    lastRunFinishedAt,
    lastRunDurationMs,
    runElapsedMs,
    codeAtLastRun,
    codeEditedSinceLastRun,
    autoClearOutput,
    setAutoClearOutput,
    clearOutput,
    stdin,
    setStdin: setStdinVal,
    files,
    setFiles: setFilesVal,
    attachmentLoadErrors,
    setAttachmentLoadErrors,
    handleRun,
    handleStop,
    outputRef,
    outputSelection,
    setOutputSelection,
    updateSelectionFromDom,
    trimOutputLines,
    fileInputRef,
    ydoc,
  };

  return <RunnerContext.Provider value={ctx}>{children}</RunnerContext.Provider>;
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ''));
    r.onerror = () => reject(r.error || new Error('read error'));
    r.readAsText(file);
  });
}

async function mergeFilesFromFileList(fileList, existingFiles, setFiles) {
  const list = Array.from(fileList || []).filter((f) => f && f.size !== undefined);
  if (!list.length) return;
  let next = { ...(existingFiles || {}) };
  for (const f of list) {
    const base = sanitizeRunnerFileKey(f.name) || 'file.txt';
    const name = uniqueRunnerFileName(base, Object.keys(next));
    const content = await readFileAsText(f);
    next = { ...next, [name]: content };
  }
  setFiles(next);
}

/** Список имён виртуальных файлов рядом с кнопкой Input в шапке редактора. */
export function EditorInputFilesBadge({ maxChars = 56 }) {
  const { files, attachmentLoadErrors } = useRunnerContext();
  const names = Object.keys(files || {}).sort((a, b) => a.localeCompare(b, 'ru-RU', { numeric: true }));
  const errs = Array.isArray(attachmentLoadErrors) ? attachmentLoadErrors : [];
  if (!names.length && !errs.length) return null;

  const okParts = names.map((n, i) => ({ kind: 'ok', id: `ok-${i}-${n}`, name: n, title: n }));
  const errParts = errs.map((e, i) => ({
    kind: 'err',
    id: `err-${i}-${e.name}`,
    name: e.name,
    title: e.message || `Не удалось загрузить «${e.name}»`,
  }));
  const full = [...names, ...errs.map((e) => e.name)].join(', ');
  const allParts = [...okParts, ...errParts];
  const joined = allParts.map((p) => p.name).join(', ');
  let truncated = false;
  let displayParts = allParts;
  if (joined.length > maxChars) {
    truncated = true;
    let acc = 0;
    displayParts = [];
    for (const p of allParts) {
      const sep = displayParts.length ? ', ' : '';
      if (acc + sep.length + p.name.length > maxChars - 1) break;
      displayParts.push(p);
      acc += sep.length + p.name.length;
    }
    if (displayParts.length === 0 && allParts.length > 0) {
      const p0 = allParts[0];
      displayParts = [
        {
          ...p0,
          name:
            p0.name.length > Math.max(1, maxChars - 1)
              ? `${p0.name.slice(0, Math.max(1, maxChars - 2))}…`
              : p0.name,
        },
      ];
    }
  }

  return (
    <span
      className="editor-input-files-badge"
      title={truncated ? full : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '0.2rem',
        fontSize: '0.78rem',
        maxWidth: '12rem',
        minWidth: 0,
      }}
    >
      {displayParts.map((p) =>
        p.kind === 'ok' ? (
          <span key={p.id} className="text-secondary" title={p.title}>
            {p.name}
          </span>
        ) : (
          <span
            key={p.id}
            className="text-danger"
            title={p.title}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}
          >
            <i className="fa-solid fa-triangle-exclamation ui-icon" style={{ fontSize: '0.7em' }} aria-hidden />
            {p.name}
          </span>
        ),
      )}
      {truncated && displayParts.length < allParts.length ? (
        <span className="text-secondary">…</span>
      ) : null}
    </span>
  );
}

/**
 * Подставляет вложения задачи в виртуальные файлы (полная замена набора).
 * @param {{ slug?: string | null, statement_attachments?: unknown } | null} task
 */
export function RunnerTaskAttachmentsSync({ task }) {
  const { setFiles, setAttachmentLoadErrors } = useRunnerContext();
  const lastKeyRef = useRef('');
  const genRef = useRef(0);

  useEffect(() => {
    const key = task?.slug || (task?.id != null ? `id:${task.id}` : '');
    if (!key) return;
    const raw = task.statement_attachments;
    const sig = `${key}\0${JSON.stringify(raw ?? null)}`;
    if (sig === lastKeyRef.current) return;
    const gen = ++genRef.current;

    (async () => {
      try {
        const attachments = Array.isArray(raw) ? raw : [];
        if (attachments.length === 0) {
          if (gen !== genRef.current) return;
          lastKeyRef.current = sig;
          setFiles({});
          setAttachmentLoadErrors([]);
          return;
        }
        const result = await fetchStatementAttachmentsAsFiles(attachments);
        if (gen !== genRef.current) return;
        lastKeyRef.current = sig;
        setFiles(result.files);
        setAttachmentLoadErrors(result.loadErrors);
      } catch (e) {
        console.error('Не удалось подставить вложения задачи:', e);
      }
    })();
  }, [task?.slug, task?.id, task?.statement_attachments, setFiles, setAttachmentLoadErrors]);
  return null;
}

/**
 * То же для совместной сессии: вложения активной задачи из ydoc sessionMeta.
 */
export function RunnerSessionTaskAttachmentsSync({ ydoc }) {
  const { setFiles, setAttachmentLoadErrors } = useRunnerContext();
  const lastSigRef = useRef('');
  const genRef = useRef(0);

  useEffect(() => {
    lastSigRef.current = '';
    genRef.current = 0;
    if (!ydoc) return;
    const meta = ydoc.getMap('sessionMeta');
    const run = () => {
      const id = meta.get('activeTaskId');
      const rawJson = meta.get('activeTaskAttachmentsJson');
      if (id === undefined && rawJson === undefined) return;

      const sig = `${id ?? ''}|${typeof rawJson === 'string' ? rawJson : ''}`;
      if (sig === lastSigRef.current) return;
      const gen = ++genRef.current;

      let attachments = [];
      try {
        attachments = typeof rawJson === 'string' && rawJson ? JSON.parse(rawJson) : [];
        if (!Array.isArray(attachments)) attachments = [];
      } catch {
        attachments = [];
      }

      (async () => {
        try {
          if (attachments.length === 0) {
            if (gen !== genRef.current) return;
            lastSigRef.current = sig;
            setFiles({});
            setAttachmentLoadErrors([]);
            return;
          }
          const result = await fetchStatementAttachmentsAsFiles(attachments);
          if (gen !== genRef.current) return;
          lastSigRef.current = sig;
          setFiles(result.files);
          setAttachmentLoadErrors(result.loadErrors);
        } catch (e) {
          console.error('Не удалось подставить вложения активной задачи:', e);
        }
      })();
    };
    run();
    meta.observe(run);
    return () => meta.unobserve(run);
  }, [ydoc, setFiles, setAttachmentLoadErrors]);
  return null;
}

export function CollaborativeRunnerInputSection() {
  const {
    stdin,
    setStdin,
    files,
    setFiles,
    fileInputRef,
  } = useRunnerContext();

  const [dropActive, setDropActive] = useState(false);
  const existingFiles = Object.keys(files || {}).sort((a, b) => a.localeCompare(b, 'ru-RU', { numeric: true }));

  const addFile = () => {
    const keys = Object.keys(files || {});
    const name = uniqueRunnerFileName('input.txt', keys);
    setFiles({ ...(files || {}), [name]: '' });
  };

  const removeFile = (name) => {
    const next = { ...(files || {}) };
    delete next[name];
    setFiles(next);
  };

  const updateFile = (name, content) => {
    setFiles({ ...(files || {}), [name]: content });
  };

  const handleFilesSelect = async (e) => {
    const list = e.target.files;
    if (!list?.length) return;
    try {
      await mergeFilesFromFileList(list, files || {}, setFiles);
    } finally {
      e.target.value = '';
    }
  };

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types?.includes('Files')) {
      e.dataTransfer.dropEffect = 'copy';
      setDropActive(true);
    }
  }, []);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) setDropActive(false);
  }, []);

  const onDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDropActive(false);
    const list = e.dataTransfer?.files;
    if (!list?.length) return;
    try {
      await mergeFilesFromFileList(list, files || {}, setFiles);
    } catch (err) {
      console.error(err);
    }
  }, [files, setFiles]);

  return (
    <div className="collab-input-section">
      <div className="input-group">
        <label>Входные данные (stdin для input()):</label>
        <textarea
          value={stdin}
          onChange={(e) => setStdin(e.target.value)}
          placeholder="Введите данные построчно..."
          rows={3}
          className="stdin-textarea"
        />
      </div>
      <div className="input-group">
        <label>Файлы (для чтения из файлов в программе):</label>
        <div
          className={`file-input-row file-input-dropzone${dropActive ? ' file-input-dropzone-active' : ''}`}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFilesSelect}
            style={{ display: 'none' }}
          />
          <button type="button" className="btn btn-outline btn-sm" onClick={() => fileInputRef.current?.click()}>
            📎 Прикрепить файлы
          </button>
          <button type="button" className="btn btn-outline btn-sm" onClick={addFile}>
            ＋ Файл вручную
          </button>
          <span className="text-secondary" style={{ fontSize: '0.85rem' }}>
            Имена в списке = имена в программе. Можно перетащить файлы сюда.
          </span>
        </div>
        {existingFiles.length === 0 ? (
          <p className="text-secondary" style={{ marginTop: '0.5rem' }}>Файлы не добавлены.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
            {existingFiles.map((name) => (
              <div key={name} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.35rem' }}>
                  <strong style={{ fontFamily: 'var(--font-code)', fontSize: '0.9rem' }}>{name}</strong>
                  <button type="button" className="btn btn-outline btn-xs btn-icon-only" title="Удалить файл" aria-label="Удалить файл" onClick={() => removeFile(name)}>
                    ✕
                  </button>
                </div>
                <textarea
                  value={files?.[name] ?? ''}
                  onChange={(e) => updateFile(name, e.target.value)}
                  placeholder={`Содержимое ${name}...`}
                  rows={3}
                  className="file-textarea"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function formatRunTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatDuration(ms) {
  if (ms == null) return '';
  if (ms < 1000) return `${ms} мс`;
  return `${(ms / 1000).toFixed(1)} с`;
}

/**
 * Строка с временем последнего запуска, длительностью и кнопкой «Очистить вывод».
 * Рендерится в заголовке блока вывода (WorkPageBlocksLayout).
 */
export function OutputBlockRunInfo() {
  const {
    loading,
    handleRun,
    handleStop,
    lastRunStartedAt,
    lastRunDurationMs,
    runElapsedMs,
    codeEditedSinceLastRun,
    clearOutput,
  } = useRunnerContext();

  const timeStr = formatRunTime(lastRunStartedAt);
  const durationStr = loading
    ? `(${formatDuration(runElapsedMs || 0)})`
    : formatDuration(lastRunDurationMs);

  const showRunMeta = Boolean(lastRunStartedAt || loading);

  return (
    <div className="output-block-run-info">
      {loading ? (
        <button
          type="button"
          className="btn btn-small work-toolbar-btn work-toolbar-run output-run-btn running"
          onClick={handleStop}
          title="Остановить программу"
          aria-label="Остановить программу"
        >
          <i className="fa-solid fa-stop ui-icon" aria-hidden />
        </button>
      ) : (
        <button
          type="button"
          className="btn btn-small work-toolbar-btn work-toolbar-run output-run-btn"
          onClick={handleRun}
          title="Запустить программу (Ctrl+Enter)"
          aria-label="Запустить программу"
        >
          <i className="fa-solid fa-play ui-icon" aria-hidden />
        </button>
      )}
      {showRunMeta && (
        <>
          {codeEditedSinceLastRun && (
            <span
              className="output-code-edited-indicator"
              title="Код изменён после последнего запуска"
              aria-label="Код изменён после последнего запуска"
            >
              ·
            </span>
          )}
          {timeStr && <span className="output-run-time">{timeStr}</span>}
          {durationStr && <span className="output-run-duration">{durationStr}</span>}
          <button
            type="button"
            className="btn btn-outline btn-sm output-clear-btn"
            onClick={clearOutput}
            title="Очистить вывод"
            aria-label="Очистить вывод"
          >
            <i className="fa-solid fa-trash" aria-hidden />
          </button>
        </>
      )}
    </div>
  );
}

export function CollaborativeRunnerOutputSection() {
  const {
    output,
    error,
    outputRef,
    outputSelection,
    updateSelectionFromDom,
  } = useRunnerContext();

  const renderOutputWithSelection = () => {
    const text = output || '';
    const { start, end } = outputSelection;
    if (!text || start === end) return text;
    const s = Math.max(0, Math.min(start, text.length));
    const e = Math.max(0, Math.min(end, text.length));
    if (s >= e) return text;
    return (
      <>
        <span>{text.slice(0, s)}</span>
        <span className="output-selection">{text.slice(s, e)}</span>
        <span>{text.slice(e)}</span>
      </>
    );
  };

  return (
    <div className="collab-output">
      {output && (
        <pre
          className="output-block"
          ref={outputRef}
          onMouseUp={updateSelectionFromDom}
          onKeyUp={updateSelectionFromDom}
        >
          {renderOutputWithSelection()}
        </pre>
      )}
      {error && (
        <pre className="output-block text-error">{error}</pre>
      )}
    </div>
  );
}
